import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["id"];
import { useEffect, useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import CompanionFooter from './CompanionFooter';
import InteractiveContentError from './InteractiveContentError';
import InteractiveEducationLoading from './InteractiveEducationLoading';
import QuizMeQuestions from './QuizMeQuestions';
import UsageTracker from '../../../utils/UsageTracker';
import { checkQuizMePollingStatus, useQuizMeHelpers, useQuizMeSession } from '../utils/quizMeUtils';
import { useAsyncGetQuizMeFromCache } from '../hooks/useGenerateAsyncQuizMe';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const QuizMeTab = ({
  additionalTracking,
  asyncGenerateError,
  asyncGenerateLoading,
  contentType,
  lessonContentId,
  language,
  onAsyncRegenerateQuiz,
  primaryContentId
}) => {
  const [questionAnswered, setQuestionAnswered] = useState(false);
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState();
  const [selectedAnswerCorrect, setSelectedAnswerCorrect] = useState();
  const progressTracking = useMemo(() => {
    const _ref = additionalTracking || {},
      {
        id
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return Object.assign({
      contentId: id
    }, rest);
  }, [additionalTracking]);
  useEffect(() => {
    if (!primaryContentId || !contentType || !additionalTracking || asyncGenerateLoading) return;else {
      UsageTracker.trackStandalone('Learning Center Interaction', Object.assign({
        action: 'ai-quiz-me-rendered',
        id: primaryContentId,
        contentType
      }, additionalTracking));
    }
  }, [primaryContentId, contentType, additionalTracking, asyncGenerateLoading]);
  const {
    incrementQuestionWithAnswer,
    resetQuiz
  } = useQuizMeSession();
  const {
    formatAsyncQuizMeQuestions
  } = useQuizMeHelpers();
  const quizMeData = useAsyncGetQuizMeFromCache({
    primaryContentId,
    lessonContentId,
    contentType,
    language
  });
  const {
    quizReady
  } = checkQuizMePollingStatus(quizMeData ? {
    tasks: [quizMeData]
  } : undefined);
  const {
    currentCorrelationId,
    quizQuestions
  } = useMemo(() => formatAsyncQuizMeQuestions({
    lessonContentId,
    contentType,
    initialQuizTasks: quizMeData ? {
      tasks: [quizMeData]
    } : undefined,
    primaryContentId
  }), [formatAsyncQuizMeQuestions, lessonContentId, contentType, quizMeData, primaryContentId]);
  const handleSelectAnswerIndexChange = (answerIndex, correct) => {
    setSelectedAnswerIndex(answerIndex);
    setSelectedAnswerCorrect(correct);
  };
  const handleCheckAnswer = () => {
    UsageTracker.trackStandalone('Content Progress', Object.assign({}, progressTracking, {
      action: 'ai-quiz-me-answered-question',
      value: selectedAnswerCorrect ? 'ai-quiz-me-answer-correct' : 'ai-quiz-me-answer-incorrect'
    }));
    incrementQuestionWithAnswer({
      contentType,
      contentId: lessonContentId !== null && lessonContentId !== void 0 ? lessonContentId : primaryContentId,
      correct: !!selectedAnswerCorrect
    });
    setQuestionAnswered(true);
  };
  const handleNext = () => {
    setQuestionAnswered(false);
    setSelectedAnswerIndex(undefined);
    setSelectedAnswerCorrect(undefined);
  };
  const handleRetakeQuiz = () => {
    UsageTracker.trackStandalone('Content Interaction', {
      action: 'ai-quiz-me-retake',
      contentId: primaryContentId,
      contentType
    });
    resetQuiz({
      contentType,
      videoContentId: lessonContentId !== null && lessonContentId !== void 0 ? lessonContentId : primaryContentId
    });
    setQuestionAnswered(false);
    setSelectedAnswerIndex(undefined);
    setSelectedAnswerCorrect(undefined);
  };
  const handleRetry = () => onAsyncRegenerateQuiz();
  const isError = !!asyncGenerateError;
  const isLoading = asyncGenerateLoading;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      backgroundColor: isError ? 'fill-surface-recessed' : undefined,
      "data-test-id": "ai-quiz-me-tab-container",
      paddingBlockStart: "700",
      paddingInline: "700",
      children: (isError || !quizReady) && !isLoading ? /*#__PURE__*/_jsx(InteractiveContentError, {
        handleRetry: handleRetry,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.quiz.quizMe"
        })
      }) : isLoading ? /*#__PURE__*/_jsx(InteractiveEducationLoading, {
        carouselTexts: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.quiz.fact1"
        }, "fact1"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.quiz.fact2"
        }, "fact2"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.quiz.fact3"
        }, "fact3"), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.quiz.fact4"
        }, "fact4")],
        mainText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.interactiveEducation.loading.buildingQuiz"
        })
      }) : /*#__PURE__*/_jsx(QuizMeQuestions, {
        additionalTracking: progressTracking,
        contentType: contentType,
        handleRestartQuiz: handleRetakeQuiz,
        handleSelectAnswerIndexChange: handleSelectAnswerIndexChange,
        lessonContentId: lessonContentId,
        primaryContentId: primaryContentId,
        questionAnswered: questionAnswered,
        quizQuestions: quizQuestions,
        selectedAnswerIndex: selectedAnswerIndex
      })
    }), /*#__PURE__*/_jsx(CompanionFooter, {
      additionalButtons: /*#__PURE__*/_jsxs(ButtonWrapper, {
        children: [!questionAnswered && selectedAnswerIndex !== undefined && /*#__PURE__*/_jsxs(Button, {
          onClick: handleCheckAnswer,
          use: "tertiary",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.interactiveEducation.quiz.checkAnswer"
          }), /*#__PURE__*/_jsx(RightIcon, {})]
        }), questionAnswered && /*#__PURE__*/_jsxs(Button, {
          onClick: handleNext,
          use: "tertiary",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.interactiveEducation.quiz.next"
          }), /*#__PURE__*/_jsx(RightIcon, {})]
        })]
      }),
      contentType: contentType,
      correlationId: currentCorrelationId,
      featureId: "QUIZ_ME",
      primaryContentId: primaryContentId,
      trackingAction: "AI_QUIZ_ME_FEEDBACK"
    })]
  });
};
export default QuizMeTab;